import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { Toast as ToastPrimitive } from '@base-ui/react/toast'
import type { ToastManagerUpdateOptions } from '@base-ui/react/toast'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'
import { Text } from '../../atoms/text/text'

/* ------------------------------------------------------------------ */
/*  Variants                                                          */
/* ------------------------------------------------------------------ */

/**
 * Stacking + slide + swipe layer.
 *
 * Inspired by Base UI's official Tailwind hero demo
 * (https://github.com/mui/base-ui/blob/HEAD/docs/src/app/(docs)/react/components/toast/demos/hero/tailwind/index.tsx)
 * — adapted for a top-center viewport where toasts slide DOWN from above
 * and stacked toasts peek out BELOW the frontmost.
 *
 * CSS variables we set per toast:
 *   --gap     gap between expanded toasts
 *   --peek    visible "peek" of each toast behind the frontmost (collapsed mode)
 *   --scale   scale factor per stack index (front=1, behind shrinks 6%/step)
 *   --shrink  1 - scale (compensation for origin-top scaling)
 *   --offset-y vertical offset when expanded (each toast lays out fixed-height + gap)
 *
 * CSS variables BaseUI provides:
 *   --toast-index           0 for frontmost, 1+ for stacked behind
 *   --toast-swipe-movement-x / -y  live swipe deltas while dragging
 */
const toastVariants = tv(
  {
    slots: {
      root: [
        // positioning — every toast stacks at the top of the viewport
        'absolute left-0 top-0 w-full will-change-transform',
        'origin-top',
        'z-[calc(1000-var(--toast-index))]',

        // stack/swipe vars
        '[--gap:12px]',
        '[--peek:12px]',
        '[--scale:calc(max(0,1-(var(--toast-index)*0.06)))]',
        '[--shrink:calc(1-var(--scale))]',
        // BaseUI measures each toast's height as --toast-height and the
        // frontmost toast's height as --toast-frontmost-height. When collapsed,
        // we clamp every toast to the front's height so the stack looks uniform
        // even if descriptions wrap to two lines on some toasts and one on others.
        '[--height:var(--toast-frontmost-height,var(--toast-height))]',
        '[--offset-y:calc(var(--toast-index)*(var(--toast-height)+var(--gap))+var(--toast-swipe-movement-y))]',

        // visual — Ømni Toasts (Figma node 528:4568)
        'shadow-dialog flex items-stretch overflow-hidden rounded-md border border-solid',

        // height — clamp to frontmost when collapsed, use own when expanded
        'h-[var(--height)] data-[expanded]:h-[var(--toast-height)]',

        // collapsed transform — stacked tightly with each toast peeking below the front
        '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]',

        // expanded transform — laid out vertically with full gap
        'data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',

        // enter — slide down from above
        'data-[starting-style]:[transform:translateY(-150%)]',

        // exit (no swipe) — slide back up
        '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',

        // exit via swipe up
        'data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'data-[expanded]:data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        // exit via swipe down
        'data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-[expanded]:data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        // exit via swipe left
        'data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-[expanded]:data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        // exit via swipe right
        'data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-[expanded]:data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',

        // opacity
        'data-[ending-style]:opacity-0',
        'data-[limited]:opacity-0',

        // transitions
        '[transition:transform_420ms_cubic-bezier(0.22,1,0.36,1),opacity_300ms,height_180ms]',
        'data-[swiping]:transition-none',

        // reduced motion — no transforms, no transitions, snap to visible
        'motion-reduce:transition-none',
        'motion-reduce:data-[starting-style]:[transform:none]!',
        'motion-reduce:[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:none]!',

        // gap-pickup pseudo so swipe gestures register in the gap between toasts
        "after:absolute after:left-0 after:top-full after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']"
      ],
      content: [
        // h-full so Content inherits the Root's clamped height; otherwise its
        // intrinsic content height would push past the Root when the front
        // toast is short and a behind toast has a longer description.
        'relative z-[1] flex h-full w-full items-start gap-4 overflow-hidden px-4 pb-6 pt-4',
        'transition-opacity duration-200',
        'data-[behind]:pointer-events-none data-[behind]:opacity-0',
        'data-[expanded]:pointer-events-auto data-[expanded]:opacity-100',
        /* The icon box equals the title's line-height (30px at the 1920 tier)
         so it stays flush with the first title line and keeps scaling with
         the responsive type matrix. Set from here rather than on the Icon:
         acqua-ui's theme declares `.material-symbols-rounded` font-size in
         the utilities layer after every Tailwind utility, so a size class on
         the icon element itself loses to it in the apps. */
        '[&>[data-slot=toast-icon]]:text-(length:--text-title-lg--line-height)'
      ],
      icon: 'shrink-0',
      // No `items-start` here — for a flex column that maps to `align-items:
      // flex-start`, which collapses block children to content width and breaks
      // `line-clamp-2` (title/description would grow to fit the text instead of
      // being clipped). Defaulting to `align-items: stretch` lets block children
      // fill the column width so the clamp can take effect.
      textCol: 'flex min-w-0 flex-1 flex-col gap-1',
      // Title and description both wrap up to 2 lines. Toasts often carry a
      // single sentence in the title slot (e.g. "Credit card fees will be
      // passed to Acme Corp."), so single-line truncation regularly loses
      // meaning. break-words handles long unbreakable strings (URLs, ids).
      title: 'line-clamp-2 break-words',
      description: 'line-clamp-2 break-words',
      action: [
        'inline-flex shrink-0 cursor-pointer items-center justify-center',
        'rounded-[2px] underline-offset-2',
        'text-body-md font-semibold',
        'hover:underline focus-visible:underline',
        'focus-visible:outline-none'
      ],
      close: [
        'flex size-6 shrink-0 cursor-pointer items-center justify-center leading-none',
        'transition-opacity hover:opacity-70',
        'focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
      ]
    },
    variants: {
      status: {
        success: {
          root: 'bg-success-bg border-success-stroke',
          icon: 'text-success-icon',
          title: 'text-success-title',
          description: 'text-success-body',
          action: 'text-success-title',
          close: 'text-success-icon'
        },
        warning: {
          root: 'bg-warning-bg border-warning-stroke',
          icon: 'text-warning-icon',
          title: 'text-warning-title',
          description: 'text-warning-body',
          action: 'text-warning-title',
          close: 'text-warning-icon'
        },
        danger: {
          root: 'bg-danger-bg border-danger-stroke',
          icon: 'text-danger-icon',
          title: 'text-danger-title',
          description: 'text-danger-body',
          action: 'text-danger-title',
          close: 'text-danger-icon'
        },
        info: {
          root: 'bg-information-bg border-information-stroke',
          icon: 'text-information-icon',
          title: 'text-information-title',
          description: 'text-information-body',
          action: 'text-information-title',
          close: 'text-information-icon'
        }
      }
    }
  },
  /* tv's bundled tailwind-merge doesn't know the Ømni text tokens, so it
     classifies `text-body-md`/`text-(length:...)` as text colors and drops
     the variant's real color class. Slots therefore render tv output as-is;
     the only merge sites are cn() where an external className comes in (the
     action slot) and Icon's own cn() for the status icon — do not "complete"
     the pattern by wrapping every slot in cn(), that just adds per-render
     merge passes for nothing. */
  { twMerge: false }
)

export type ToastStatus = NonNullable<
  VariantProps<typeof toastVariants>['status']
>

/** @deprecated Use `danger`. Kept so existing `status: 'error'` callers compile. */
export type LegacyToastStatus = 'error'

/* ------------------------------------------------------------------ */
/*  Status → icon mapping                                             */
/* ------------------------------------------------------------------ */

const statusIconName: Record<ToastStatus, string> = {
  success: 'check_circle',
  warning: 'warning',
  danger: 'error',
  info: 'info'
}

const DEFAULT_TIMEOUT_MS = 5000
const ToastTimeoutContext = createContext<number | undefined>(undefined)

const normalizeStatus = (
  status: ToastStatus | LegacyToastStatus
): ToastStatus => (status === 'error' ? 'danger' : status)

/* ------------------------------------------------------------------ */
/*  Custom data carried on each toast object                          */
/* ------------------------------------------------------------------ */

export interface ToastActionConfig {
  label: ReactNode
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick']
  /** Additional props forwarded to the underlying `<button>`. */
  buttonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>
}

interface AcquaToastData {
  status: ToastStatus
  showClose: boolean
  showIcon: boolean
  iconName?: string
  expiresAt?: number
  fullDescription?: boolean
}

export interface ToastAddOptions {
  status?: ToastStatus | LegacyToastStatus
  title?: ReactNode
  description?: ReactNode
  showClose?: boolean
  /** Hides the status icon when `false`. */
  showIcon?: boolean
  /** Material Symbols name overriding the status default icon. */
  icon?: string
  /** Shows the whole description instead of clamping it to two lines. */
  fullDescription?: boolean
  action?: ToastActionConfig
  /** Auto-dismiss delay in ms. Hover/focus defers removal, not expiry. `0` disables it. */
  timeout?: number
  priority?: 'low' | 'high'
  onClose?: () => void
  onRemove?: () => void
  id?: string
}

export type ToastUpdateOptions = Partial<Omit<ToastAddOptions, 'id'>>

/* ------------------------------------------------------------------ */
/*  Renderer for a single toast                                       */
/* ------------------------------------------------------------------ */

interface ToastItemProps {
  toast: ReturnType<
    typeof ToastPrimitive.useToastManager<AcquaToastData>
  >['toasts'][number]
}

// Static render targets — hoisted so BaseUI's render-prop handling isn't fed
// a new element identity on every ToastItem render.
const TITLE_TEXT = <Text as='h2' variant='title-lg' />
const DESCRIPTION_TEXT = <Text as='p' variant='body-md' />

function ToastItem({ toast }: ToastItemProps) {
  const { close } = ToastPrimitive.useToastManager<AcquaToastData>()
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const expiresAt = toast.data?.expiresAt

  useEffect(() => {
    if (
      expiresAt === undefined ||
      toast.type === 'loading' ||
      toast.transitionStatus === 'ending' ||
      hovered ||
      focused
    ) {
      return
    }

    // The deadline stays fixed while interacting; expired toasts close on leave.
    const timer = setTimeout(
      () => close(toast.id),
      Math.max(0, expiresAt - Date.now())
    )
    return () => clearTimeout(timer)
  }, [
    close,
    expiresAt,
    focused,
    hovered,
    toast.id,
    toast.transitionStatus,
    toast.type
  ])

  const status: ToastStatus = toast.data?.status ?? 'info'
  const showClose = toast.data?.showClose ?? false
  const showIcon = toast.data?.showIcon ?? true
  const iconName = toast.data?.iconName ?? statusIconName[status]
  const fullDescription = toast.data?.fullDescription ?? false

  const slots = toastVariants({ status })

  return (
    <ToastPrimitive.Root
      toast={toast}
      data-slot='toast'
      data-status={status}
      className={slots.root()}
      swipeDirection={['up', 'left', 'right']}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false)
        }
      }}
    >
      <ToastPrimitive.Content
        data-slot='toast-content'
        className={slots.content()}
      >
        {showIcon ? (
          <Icon
            name={iconName}
            data-slot='toast-icon'
            className={slots.icon()}
          />
        ) : null}

        <div data-slot='toast-text' className={slots.textCol()}>
          {toast.title ? (
            <ToastPrimitive.Title
              data-slot='toast-title'
              className={slots.title()}
              render={TITLE_TEXT}
            />
          ) : null}
          {toast.description ? (
            <ToastPrimitive.Description
              data-slot='toast-description'
              className={cn(
                slots.description(),
                fullDescription && 'line-clamp-none'
              )}
              render={DESCRIPTION_TEXT}
            />
          ) : null}
        </div>

        {toast.actionProps ? (
          <ToastPrimitive.Action
            data-slot='toast-action'
            className={cn(slots.action(), toast.actionProps.className)}
          />
        ) : null}

        {showClose ? (
          <ToastPrimitive.Close
            data-slot='toast-close'
            aria-label='Dismiss notification'
            className={slots.close()}
          >
            <Icon name='close' size='lg' />
          </ToastPrimitive.Close>
        ) : null}
      </ToastPrimitive.Content>
    </ToastPrimitive.Root>
  )
}

/* ------------------------------------------------------------------ */
/*  ToastList — renders every active toast inside the Viewport        */
/* ------------------------------------------------------------------ */

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager<AcquaToastData>()
  return (
    <>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Toaster — drop-in setup                                           */
/* ------------------------------------------------------------------ */

export interface ToasterProps {
  /** Maximum number of toasts visible at once. */
  limit?: number
  /**
   * Default auto-dismiss delay in ms (5000). Set `0` to disable.
   * Hover or keyboard focus defers removal until interaction ends;
   * it does not extend the deadline. Window blur does not pause expiry.
   */
  timeout?: number
  /** Optional class override for the viewport container. */
  viewportClassName?: string
  /**
   * Components rendered inside the provider. Any `useToast()` call must
   * be in this subtree. Mount `<Toaster>` near the root of your app and
   * wrap your application with it (or render it alongside your app inside
   * a separate `<Toast.Provider>` for advanced setups).
   */
  children?: ReactNode
}

// Viewport sits centered at the top of the screen. Toasts inside are
// position:absolute so they overlap (collapsed) and animate via the
// transforms defined on Root. Width matches the Ømni toast card (512px,
// Figma node 528:4567) so the stacked cards align edge-to-edge.
const viewportClasses = [
  'pointer-events-none fixed top-4 left-1/2 z-[60]',
  '-translate-x-1/2 w-[512px]',
  '[&>*]:pointer-events-auto'
].join(' ')

export function Toaster({
  limit = 3,
  timeout = DEFAULT_TIMEOUT_MS,
  viewportClassName,
  children
}: ToasterProps) {
  return (
    <ToastPrimitive.Provider limit={limit} timeout={0}>
      <ToastTimeoutContext.Provider value={timeout}>
        {children}
        <ToastPrimitive.Portal>
          <ToastPrimitive.Viewport
            data-slot='toast-viewport'
            className={cn(viewportClasses, viewportClassName)}
          >
            <ToastList />
          </ToastPrimitive.Viewport>
        </ToastPrimitive.Portal>
      </ToastTimeoutContext.Provider>
    </ToastPrimitive.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  useToast — typed wrapper around useToastManager                   */
/* ------------------------------------------------------------------ */

function actionToProps(
  action?: ToastActionConfig
): ButtonHTMLAttributes<HTMLButtonElement> | undefined {
  if (!action) return undefined
  const { label, onClick, buttonProps } = action
  return {
    ...buttonProps,
    children: label,
    onClick
  }
}

function buildAddOptions(opts: ToastAddOptions, managedTimeout?: number) {
  const {
    status = 'info',
    showClose = false,
    showIcon = true,
    icon,
    fullDescription = false,
    title,
    description,
    action,
    timeout,
    priority,
    onClose,
    onRemove,
    id
  } = opts
  const resolvedStatus = normalizeStatus(status)
  const duration = timeout ?? managedTimeout ?? DEFAULT_TIMEOUT_MS
  return {
    id,
    title,
    description,
    // Toaster owns wall-clock expiry; advanced primitive providers keep Base UI timers.
    timeout:
      managedTimeout === undefined
        ? timeout ?? (showClose ? DEFAULT_TIMEOUT_MS : undefined)
        : 0,
    priority,
    onClose,
    onRemove,
    actionProps: actionToProps(action),
    data: {
      status: resolvedStatus,
      showClose,
      showIcon,
      iconName: icon,
      fullDescription,
      expiresAt:
        managedTimeout !== undefined && duration > 0
          ? Date.now() + duration
          : undefined
    }
  }
}

function buildUpdateOptions(opts: ToastUpdateOptions, managedTimeout?: number) {
  const out: {
    title?: ReactNode
    description?: ReactNode
    timeout?: number
    priority?: 'low' | 'high'
    onClose?: () => void
    onRemove?: () => void
    actionProps?: ButtonHTMLAttributes<HTMLButtonElement>
    data?: Partial<AcquaToastData>
  } = {}
  if ('title' in opts) out.title = opts.title
  if ('description' in opts) out.description = opts.description
  if (opts.timeout !== undefined) {
    out.timeout = managedTimeout === undefined ? opts.timeout : 0
  }
  if (opts.priority !== undefined) out.priority = opts.priority
  if (opts.onClose !== undefined) out.onClose = opts.onClose
  if (opts.onRemove !== undefined) out.onRemove = opts.onRemove
  if (opts.action !== undefined) out.actionProps = actionToProps(opts.action)
  const dataPatch: Partial<AcquaToastData> = {}
  if (managedTimeout !== undefined && opts.timeout !== undefined) {
    dataPatch.expiresAt =
      opts.timeout > 0 ? Date.now() + opts.timeout : undefined
  }
  if (opts.status !== undefined) dataPatch.status = normalizeStatus(opts.status)
  if (opts.showClose !== undefined) dataPatch.showClose = opts.showClose
  if (opts.showIcon !== undefined) dataPatch.showIcon = opts.showIcon
  if ('icon' in opts) dataPatch.iconName = opts.icon
  if (opts.fullDescription !== undefined)
    dataPatch.fullDescription = opts.fullDescription
  if (Object.keys(dataPatch).length > 0) out.data = dataPatch
  return out
}

export function useToast() {
  const managedTimeout = useContext(ToastTimeoutContext)
  /* BaseUI's store methods are class-property arrows on an instance created
     once per Provider, so each is a permanently stable reference — only
     `toasts` changes. Destructuring lets them be honest `useCallback` deps
     instead of an empty array propped up by a ref. */
  const {
    add: addToast,
    close: closeToast,
    update: updateToast,
    promise: promiseToast,
    toasts
  } = ToastPrimitive.useToastManager<AcquaToastData>()

  /* The one read that genuinely needs latest-value semantics: `update` merges
     the toast's previous `data`, so it has to see the current list. */
  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  /** Add a new toast. Returns its id. */
  const add = useCallback(
    (opts: ToastAddOptions): string =>
      addToast(buildAddOptions(opts, managedTimeout)) as string,
    [addToast, managedTimeout]
  )

  /** Update an existing toast by id. */
  const update = useCallback(
    (id: string, opts: ToastUpdateOptions): void => {
      const built = buildUpdateOptions(opts, managedTimeout)
      if (built.data) {
        /* Base UI's updateToast shallow-spreads the toast, replacing `data`
           wholesale — merge the previous data in so a status-only update
           doesn't silently drop showClose/showIcon/iconName. */
        const prev = toastsRef.current.find(t => t.id === id)?.data
        built.data = { ...prev, ...built.data }
      }
      updateToast(id, built as ToastManagerUpdateOptions<AcquaToastData>)
    },
    [updateToast, managedTimeout]
  )

  /** Dismiss a toast by id. */
  const close = useCallback((id: string): void => closeToast(id), [closeToast])

  /**
   * Bind a Promise to a toast that transitions from loading → success/error.
   * String values are used as the toast title; object values are full update payloads.
   */
  const promise = useCallback(
    <Value,>(
      input: Promise<Value>,
      messages: ToastPromiseMessages<Value>
    ): Promise<Value> => {
      const toUpdate = (
        m: string | ToastUpdateOptions,
        previousData?: Partial<AcquaToastData>
      ): ToastManagerUpdateOptions<AcquaToastData> => {
        const options = typeof m === 'string' ? { title: m } : m
        const built = buildUpdateOptions(
          managedTimeout === undefined
            ? options
            : { ...options, timeout: options.timeout ?? managedTimeout },
          managedTimeout
        )
        if (previousData) built.data = { ...previousData, ...built.data }
        return built as ToastManagerUpdateOptions<AcquaToastData>
      }

      const loading = toUpdate(messages.loading)
      return promiseToast(input, {
        loading,
        success: (result: Value) => {
          const m =
            typeof messages.success === 'function'
              ? messages.success(result)
              : messages.success
          return toUpdate(m, loading.data)
        },
        error: (err: unknown) => {
          const m =
            typeof messages.error === 'function'
              ? messages.error(err)
              : messages.error
          return toUpdate(m, loading.data)
        }
      })
    },
    [promiseToast, managedTimeout]
  )

  /* `toasts` is a getter rather than a captured field: the list changes on
     every toast, and capturing it here would churn this object's identity —
     the exact thing consumers depend on staying put when they list the hook's
     result in a dependency array. */
  return useMemo(
    () => ({
      /** The current array of active toasts. */
      get toasts() {
        return toastsRef.current
      },
      add,
      update,
      close,
      promise
    }),
    [add, update, close, promise]
  )
}

export interface ToastPromiseMessages<Value> {
  loading: string | ToastUpdateOptions
  success:
    | string
    | ToastUpdateOptions
    | ((value: Value) => string | ToastUpdateOptions)
  error:
    | string
    | ToastUpdateOptions
    | ((err: unknown) => string | ToastUpdateOptions)
}

/* ------------------------------------------------------------------ */
/*  Toast namespace — access to the underlying BaseUI primitives      */
/*  for advanced custom rendering.                                    */
/* ------------------------------------------------------------------ */

interface ToastNamespace {
  Provider: typeof ToastPrimitive.Provider
  Viewport: typeof ToastPrimitive.Viewport
  Root: typeof ToastPrimitive.Root
  Content: typeof ToastPrimitive.Content
  Title: typeof ToastPrimitive.Title
  Description: typeof ToastPrimitive.Description
  Action: typeof ToastPrimitive.Action
  Close: typeof ToastPrimitive.Close
}

export const Toast: ToastNamespace = {
  Provider: ToastPrimitive.Provider,
  Viewport: ToastPrimitive.Viewport,
  Root: ToastPrimitive.Root,
  Content: ToastPrimitive.Content,
  Title: ToastPrimitive.Title,
  Description: ToastPrimitive.Description,
  Action: ToastPrimitive.Action,
  Close: ToastPrimitive.Close
}
